import { useCallback, useEffect, useRef, useState } from 'react';
import type { User, UserRole } from '../types/auth.types';
import { isPlatformRole } from '../types/auth.types';
import {
  permissionService,
  type ResolvedInstitutionPermissions,
} from '../services/permissionService';
import { workspaceService } from '../services/workspaceService';
import type { WorkspaceDescriptor } from '../types/workspace.types';

export interface WorkspaceAuthorizationUser extends User {
  institutionId?: string;
  institutions?: string[];
  originalRole?: UserRole;
  originalInstitutionId?: string;
}

interface UseWorkspaceAuthorizationOptions {
  effectiveRole: UserRole | undefined;
  effectiveInstitutionId: string | undefined;
  onInstitutionCommitted: (
    accountUser: WorkspaceAuthorizationUser,
    resolved: ResolvedInstitutionPermissions,
  ) => void;
  onPlatformCommitted: (accountUser: WorkspaceAuthorizationUser) => void;
}

export const resolveSelectedWorkspaceKey = (
  workspaces: WorkspaceDescriptor[],
  role: UserRole | undefined,
  institutionId: string | undefined,
): string | null => {
  if (institutionId) {
    const institutionWorkspace = workspaces.find(
      (workspace) =>
        workspace.kind === 'institution' && workspace.institutionId === institutionId,
    );
    return institutionWorkspace?.key ?? null;
  }

  if (!role || !isPlatformRole(role)) {
    return null;
  }

  return workspaces.some((workspace) => workspace.kind === 'platform')
    ? 'platform'
    : null;
};

const isDiscoveredWorkspace = (
  workspace: WorkspaceDescriptor,
  discoveredWorkspaces: WorkspaceDescriptor[],
): boolean =>
  discoveredWorkspaces.some(
    (discoveredWorkspace) =>
      discoveredWorkspace.key === workspace.key
      && discoveredWorkspace.kind === workspace.kind
      && (
        workspace.kind === 'platform'
        || (
          discoveredWorkspace.kind === 'institution'
          && discoveredWorkspace.institutionId === workspace.institutionId
        )
      ),
  );

export const useWorkspaceAuthorization = ({
  effectiveRole,
  effectiveInstitutionId,
  onInstitutionCommitted,
  onPlatformCommitted,
}: UseWorkspaceAuthorizationOptions) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceDescriptor[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [workspaceSwitching, setWorkspaceSwitching] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [selectedWorkspaceKey, setSelectedWorkspaceKey] = useState<string | null>(null);

  const accountUserRef = useRef<WorkspaceAuthorizationUser | null>(null);
  const authenticationGenerationRef = useRef(0);
  const workspaceRequestRef = useRef(0);
  const workspaceSwitchRequestRef = useRef(0);

  const clearWorkspaceState = useCallback((): void => {
    workspaceRequestRef.current += 1;
    workspaceSwitchRequestRef.current += 1;
    setWorkspaces([]);
    setWorkspacesLoading(false);
    setWorkspaceSwitching(false);
    setWorkspaceError(null);
    setSelectedWorkspaceKey(null);
  }, []);

  const loadWorkspaces = useCallback(async (
    accountUser: WorkspaceAuthorizationUser,
    authenticationGeneration: number,
  ): Promise<void> => {
    const requestId = workspaceRequestRef.current + 1;
    workspaceRequestRef.current = requestId;
    setWorkspaces([]);
    setWorkspacesLoading(true);
    setWorkspaceError(null);
    setSelectedWorkspaceKey(null);

    const isCurrentRequest = (): boolean =>
      workspaceRequestRef.current === requestId
      && authenticationGenerationRef.current === authenticationGeneration
      && accountUserRef.current?.id === accountUser.id;

    try {
      const discovery = await workspaceService.getWorkspaces();
      if (!isCurrentRequest()) return;

      if (discovery.userId !== accountUser.id) {
        throw new Error('Workspace discovery response does not match the authenticated user');
      }

      setWorkspaces(discovery.workspaces);
    } catch (err) {
      if (!isCurrentRequest()) return;
      setWorkspaces([]);
      setWorkspaceError(
        err instanceof Error ? err.message : 'Failed to discover workspaces',
      );
    } finally {
      if (isCurrentRequest()) {
        setWorkspacesLoading(false);
      }
    }
  }, []);

  const setAuthenticatedWorkspaceUser = useCallback((
    accountUser: WorkspaceAuthorizationUser,
  ): void => {
    authenticationGenerationRef.current += 1;
    accountUserRef.current = accountUser;
    clearWorkspaceState();
    void loadWorkspaces(accountUser, authenticationGenerationRef.current);
  }, [clearWorkspaceState, loadWorkspaces]);

  const clearAuthenticatedWorkspaceUser = useCallback((): void => {
    authenticationGenerationRef.current += 1;
    accountUserRef.current = null;
    clearWorkspaceState();
  }, [clearWorkspaceState]);

  const refetchWorkspaces = useCallback(async (): Promise<void> => {
    const accountUser = accountUserRef.current;
    if (!accountUser) {
      clearWorkspaceState();
      return;
    }

    await loadWorkspaces(accountUser, authenticationGenerationRef.current);
  }, [clearWorkspaceState, loadWorkspaces]);

  useEffect(() => {
    setSelectedWorkspaceKey(
      resolveSelectedWorkspaceKey(
        workspaces,
        effectiveRole,
        effectiveInstitutionId,
      ),
    );
  }, [effectiveInstitutionId, effectiveRole, workspaces]);

  const selectWorkspace = useCallback(async (
    workspace: WorkspaceDescriptor,
  ): Promise<boolean> => {
    const accountUser = accountUserRef.current;
    const authenticationGeneration = authenticationGenerationRef.current;

    if (!accountUser || !isDiscoveredWorkspace(workspace, workspaces)) {
      setWorkspaceError('The selected workspace is not available for this account');
      return false;
    }

    const switchRequestId = workspaceSwitchRequestRef.current + 1;
    workspaceSwitchRequestRef.current = switchRequestId;
    setWorkspaceSwitching(true);
    setWorkspaceError(null);

    const isCurrentSwitch = (): boolean =>
      workspaceSwitchRequestRef.current === switchRequestId
      && authenticationGenerationRef.current === authenticationGeneration
      && accountUserRef.current?.id === accountUser.id;

    try {
      if (workspace.kind === 'platform') {
        if (!isPlatformRole(accountUser.role)) {
          throw new Error('Platform workspace is not available for this account');
        }

        if (!isCurrentSwitch()) return false;

        onPlatformCommitted(accountUser);
        setSelectedWorkspaceKey(workspace.key);
        return true;
      }

      const resolved = await permissionService.resolveAuthenticatedUserPermissions(
        accountUser.id,
        workspace.institutionId,
      );
      if (!isCurrentSwitch()) return false;

      onInstitutionCommitted(accountUser, resolved);
      setSelectedWorkspaceKey(workspace.key);
      return true;
    } catch (err) {
      if (!isCurrentSwitch()) return false;
      setWorkspaceError(
        err instanceof Error ? err.message : 'Failed to switch workspace',
      );
      return false;
    } finally {
      if (isCurrentSwitch()) {
        setWorkspaceSwitching(false);
      }
    }
  }, [onInstitutionCommitted, onPlatformCommitted, workspaces]);

  return {
    workspaces,
    workspacesLoading,
    workspaceSwitching,
    workspaceError,
    selectedWorkspaceKey,
    selectWorkspace,
    refetchWorkspaces,
    setAuthenticatedWorkspaceUser,
    clearAuthenticatedWorkspaceUser,
  };
};
